import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { type ApiResponseUnreadCount } from "@vega-api/generated/types/apiResponseUnreadCount";
import { type ListNotificationsParams } from "@vega-api/generated/types/listNotificationsParams";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import {
  handleMessagingStreamEvent,
  invalidateMessagingStreamCaches,
} from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";

import { dispatchNotification } from "./dispatchNotification";
import { emitNotificationEvent } from "./notificationEventBus";
import { type NotificationsListInfinite } from "./notificationsCache";
import {
  getPublisherUnreadCountQueryKey,
  metaContainsFilter,
  METADATA_UNREAD_COUNT_BASE_KEY,
  NOTIFICATIONS_LIST_BASE_KEY,
  readCacheMetadataFilter,
} from "./notificationScope";
import { connectNotificationsStream } from "./notificationsStreamClient";
import {
  NOTIFICATION_TOAST_DURATION_MS,
  NotificationToast,
  NotificationToastTransition,
} from "./NotificationToast";
import { useNotificationsPatchMutation } from "./useNotificationsPatchMutation";

type Props = {
  enabled: boolean;
  /** Restricts the stream to one publishing app (e.g. "coinvest-deals"). */
  publisher?: string;
  showToast?: boolean;
};

export const useNotificationsStream = ({
  enabled,
  publisher,
  showToast = true,
}: Props) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scope = useMemo(() => (publisher ? { publisher } : {}), [publisher]);
  const { mutate: patchNotificationMutation } =
    useNotificationsPatchMutation(scope);

  useEffect(() => {
    if (!enabled) return;

    const unreadCountQueryKey = getPublisherUnreadCountQueryKey(scope);

    const adjustUnreadCount = (delta: number) => {
      if (delta === 0) return;
      queryClient.setQueryData<ApiResponseUnreadCount>(
        unreadCountQueryKey,
        (current) => {
          if (!current?.data) return current;

          return {
            ...current,
            data: { count: Math.max(0, current.data.count + delta) },
          };
        },
      );
    };

    const matchedListCaches = () =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey: NOTIFICATIONS_LIST_BASE_KEY });

    const findCached = (id: string) =>
      matchedListCaches()
        .flatMap(
          (query) =>
            queryClient.getQueryData<NotificationsListInfinite>(query.queryKey)
              ?.pages ?? [],
        )
        .flatMap((page) => page.data)
        .find((item) => item.id === id);

    const refreshMetadataCounts = () => {
      queryClient.invalidateQueries({
        queryKey: METADATA_UNREAD_COUNT_BASE_KEY,
      });
    };

    const reconcile = () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_BASE_KEY });
      queryClient.invalidateQueries({ queryKey: unreadCountQueryKey });
      void invalidateMessagingStreamCaches(queryClient);
    };

    const handleCreated = (notification: NotificationResponse) => {
      // Drop transient signals and events for other publishers.
      if (!notification.id) return;

      if (publisher && notification.publisher !== publisher) return;

      const alreadyCached = findCached(notification.id);

      if (!alreadyCached && showToast) {
        toast(
          ({ closeToast }) => (
            <NotificationToast
              closeToast={closeToast}
              notification={notification}
              onClick={() => {
                patchNotificationMutation({
                  id: notification.id,
                  data: { read: true },
                });

                const url = dispatchNotification(notification);

                if (url)
                  navigate(url, { state: { notificationId: notification.id } });

                closeToast();
              }}
            />
          ),
          {
            position: "bottom-left",
            autoClose: NOTIFICATION_TOAST_DURATION_MS,
            closeButton: false,
            icon: false,
            className: "w-96",
            transition: NotificationToastTransition,
          },
        );
      }

      // Prepend to every matching list cache
      matchedListCaches().forEach((query) => {
        const cacheFilter = readCacheMetadataFilter(
          query.queryKey[1] as ListNotificationsParams | undefined,
        );

        if (cacheFilter && !metaContainsFilter(notification.meta, cacheFilter))
          return;

        queryClient.setQueryData<NotificationsListInfinite>(
          query.queryKey,
          (current) => {
            if (!current?.pages.length) return current;

            const alreadyInPages = current.pages.some((page) =>
              page.data.some((item) => item.id === notification.id),
            );

            if (alreadyInPages) return current;

            return {
              ...current,
              pages: current.pages.map((page, index) =>
                index === 0
                  ? { ...page, data: [notification, ...page.data] }
                  : page,
              ),
            };
          },
        );
      });

      if (!alreadyCached && !notification.readAt) adjustUnreadCount(1);
      refreshMetadataCounts();
    };

    const handleUpdated = (notification: NotificationResponse) => {
      if (!notification.id) return;

      if (publisher && notification.publisher !== publisher) return;

      const before = findCached(notification.id);

      matchedListCaches().forEach((query) => {
        queryClient.setQueryData<NotificationsListInfinite>(
          query.queryKey,
          (current) => {
            if (!current?.pages.length) return current;

            return {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: page.data.map((item) =>
                  item.id === notification.id ? notification : item,
                ),
              })),
            };
          },
        );
      });

      if (before && !before.readAt && notification.readAt) {
        adjustUnreadCount(-1);
      } else if (before && before.readAt && !notification.readAt) {
        adjustUnreadCount(1);
      }
      refreshMetadataCounts();
    };

    const handleDeleted = (id: string) => {
      const removed = findCached(id);

      matchedListCaches().forEach((query) => {
        queryClient.setQueryData<NotificationsListInfinite>(
          query.queryKey,
          (current) => {
            if (!current?.pages.length) return current;

            return {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: page.data.filter((item) => item.id !== id),
              })),
            };
          },
        );
      });

      if (removed && !removed.readAt) adjustUnreadCount(-1);
      refreshMetadataCounts();
    };

    return connectNotificationsStream({
      onOpen: reconcile,
      onEvent: (eventName, data) => {
        switch (eventName) {
          case "notification.created": {
            const payload = data as NotificationResponse;

            emitNotificationEvent(payload);
            handleCreated(payload);
            break;
          }

          case "notification.updated":
            handleUpdated(data as NotificationResponse);
            break;

          case "notification.deleted": {
            const payload = data as { id?: string };

            if (payload.id) handleDeleted(payload.id);
            break;
          }

          // TODO(VD-28584): decouple co-invest messaging from this generic
          // stream via notificationEventBus once subscribers grow — an
          // earlier extraction broke live-refresh, so it stays inline for now.
          case "message.created":
          case "message.replied":
          case "conversation.created":
          case "conversation.updated":
            handleMessagingStreamEvent(queryClient, data);
            break;

          default:
            break;
        }
      },
    });
  }, [
    enabled,
    publisher,
    showToast,
    scope,
    queryClient,
    navigate,
    patchNotificationMutation,
  ]);
};
